import React from 'react';
import {
    Box,
    Pagination,
    PaginationItem,
    Typography,
    Stack,
    IconButton,
    Tooltip
} from '@mui/material';
import CustomDropdown from './CustomDropdown';
import { SecondaryButton } from './Buttons';
import {
    FirstPage,
    LastPage,
    NavigateBefore,
    NavigateNext,
    Refresh
} from '@mui/icons-material';

const KQSPLPagination = ({
    count,
    page,
    pageSize,
    totalPages,
    onPageChange,
    onPageSizeChange,
    onRefresh,
    showPageSizeSelector = true,
    showRefreshButton = true,
    showInfo = true,
    disabled = false,
    pageSizeOptions = [10, 25, 50, 100],
    sx = {}
}) => {
    // Ensure page size options is a valid array with no undefined entries (avoids .value errors in dropdown)
    const baseOptions = Array.isArray(pageSizeOptions) ? pageSizeOptions.filter((n) => n != null) : [10, 25, 50, 100];
    // Include current pageSize so MUI Select always has a matching option (avoids "reading 'value' of undefined" in SelectInput)
    const safePageSizeOptions = baseOptions.includes(pageSize)
        ? baseOptions
        : [...baseOptions, pageSize].sort((a, b) => Number(a) - Number(b));
    const handlePageChange = (event, newPage) => {
        if (onPageChange && newPage !== page) {
            onPageChange(newPage);
        }
    };

    const handlePageSizeChange = (event) => {
        if (onPageSizeChange) {
            onPageSizeChange(event.target.value);
        }
    };

    const handleRefresh = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const getDisplayInfo = () => {
        if (!count) return 'No items';
        
        const start = (page - 1) * pageSize + 1;
        const end = Math.min(page * pageSize, count);
        
        return `${start}-${end} of ${count} items`;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2,
                px: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                ...sx
            }}
        >
            {/* Left side - Info and Refresh */}
            <Stack direction="row" spacing={2} alignItems="center">
                {showInfo && (
                    <Typography variant="body2" color="text.secondary">
                        {getDisplayInfo()}
                    </Typography>
                )}
                
                {showRefreshButton && (
                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={disabled}
                            size="small"
                        >
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* Center - Pagination */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    disabled={disabled}
                    showFirstButton
                    showLastButton
                    size="medium"
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            slots={{
                                first: FirstPage,
                                last: LastPage,
                                previous: NavigateBefore,
                                next: NavigateNext,
                            }}
                        />
                    )}
                />
            </Box>

            {/* Right side - Page Size Selector */}
            {showPageSizeSelector && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        Items per page:
                    </Typography>
                    <CustomDropdown
                        size="small"
                        label=""
                        value={pageSize}
                        onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
                        options={safePageSizeOptions.map((size) => ({ value: size, label: String(size) }))}
                        fullWidth={false}
                        disabled={disabled}
                        sx={{ minWidth: 80 }}
                    />
                </Stack>
            )}
        </Box>
    );
};

// Compact pagination for smaller spaces
export const CompactPagination = ({
    count,
    page,
    pageSize,
    totalPages,
    onPageChange,
    onPageSizeChange,
    onRefresh,
    disabled = false,
    pageSizeOptions = [10, 25, 50],
    sx = {}
}) => {
    const handlePageChange = (event, newPage) => {
        if (onPageChange && newPage !== page) {
            onPageChange(newPage);
        }
    };

    const handlePageSizeChange = (event) => {
        if (onPageSizeChange) {
            onPageSizeChange(event.target.value);
        }
    };

    const handleRefresh = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                px: 2,
                ...sx
            }}
        >
            {/* Page Info */}
            <Typography variant="caption" color="text.secondary">
                Page {page} of {totalPages} ({count} total)
            </Typography>

            {/* Navigation Buttons */}
            <Stack direction="row" spacing={1}>
                <SecondaryButton
                    size="small"
                    onClick={() => onPageChange(1)}
                    disabled={disabled || page === 1}
                    startIcon={<FirstPage />}
                >
                    First
                </SecondaryButton>
                <SecondaryButton
                    size="small"
                    onClick={() => onPageChange(page - 1)}
                    disabled={disabled || page === 1}
                    startIcon={<NavigateBefore />}
                >
                    Prev
                </SecondaryButton>
                <SecondaryButton
                    size="small"
                    onClick={() => onPageChange(page + 1)}
                    disabled={disabled || page === totalPages}
                    endIcon={<NavigateNext />}
                >
                    Next
                </SecondaryButton>
                <SecondaryButton
                    size="small"
                    onClick={() => onPageChange(totalPages)}
                    disabled={disabled || page === totalPages}
                    endIcon={<LastPage />}
                >
                    Last
                </SecondaryButton>
            </Stack>

            {/* Page Size */}
            <CustomDropdown
                size="small"
                label=""
                value={pageSize}
                onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
                options={pageSizeOptions.map((size) => ({ value: size, label: String(size) }))}
                fullWidth={false}
                disabled={disabled}
                sx={{ minWidth: 70 }}
            />
        </Box>
    );
};

// Infinite scroll pagination
export const InfiniteScrollPagination = ({
    hasNext,
    onLoadMore,
    loading = false,
    sx = {}
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 2,
                ...sx
            }}
        >
            {hasNext && (
                <SecondaryButton
                    onClick={onLoadMore}
                    disabled={loading}
                    startIcon={loading ? <Refresh /> : null}
                >
                    {loading ? 'Loading…' : 'Load more'}
                </SecondaryButton>
            )}
        </Box>
    );
};

export default KQSPLPagination; 